function sanitizeNumericDraft(raw, { integer = false } = {}) {
  const source = raw == null ? '' : String(raw);
  let seenDecimal = false;
  let result = '';
  for (const char of source) {
    if (char === ' ' || char === '\u00A0' || char === '\u202F') continue;
    if (char === '-' && result === '') {
      result = '-';
      continue;
    }
    if (char >= '0' && char <= '9') {
      result += char;
      continue;
    }
    if (!integer && (char === '.' || char === ',') && !seenDecimal) {
      seenDecimal = true;
      result += '.';
    }
  }
  return result;
}

/**
 * Parse a user-typed numeric string.
 * Distinguishes empty, incomplete prefixes ("-", ".", "2."), and real numbers
 * so the field can stay blank while editing.
 *
 * @param {unknown} raw
 * @param {{ integer?: boolean }} [options]
 * @returns {{ kind: 'empty' } | { kind: 'incomplete', value?: number } | { kind: 'invalid' } | { kind: 'number', value: number }}
 */
export function parseEmptyableNumber(raw, { integer = false } = {}) {
  const draft = sanitizeNumericDraft(raw, { integer });
  if (draft === '') return { kind: 'empty' };
  if (draft === '-' || draft === '.' || draft === '-.') return { kind: 'incomplete' };
  if (!integer && /\.$/.test(draft)) {
    const value = parseFloat(draft);
    if (!Number.isFinite(value)) return { kind: 'incomplete' };
    return { kind: 'incomplete', value };
  }
  if (integer) {
    if (!/^-?\d+$/.test(draft)) return { kind: 'invalid' };
    const value = parseInt(draft, 10);
    if (!Number.isFinite(value)) return { kind: 'invalid' };
    return { kind: 'number', value };
  }
  const value = parseFloat(draft);
  if (!Number.isFinite(value)) return { kind: 'invalid' };
  return { kind: 'number', value };
}

export function formatEmptyableNumber(value, { integer = false } = {}) {
  if (value === '' || value === null || value === undefined) return '';
  const number = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(number)) return '';
  if (integer) return String(number);
  return String(number);
}

export function clampEmptyableNumber(value, min, max) {
  let next = value;
  if (typeof min === 'number' && next < min) next = min;
  if (typeof max === 'number' && next > max) next = max;
  return next;
}

export function resolveEmptyableCommit(
  raw,
  { integer = false, allowEmpty = true, min, max, clamp = false } = {},
) {
  const parsed = parseEmptyableNumber(raw, { integer });
  if (parsed.kind === 'empty' || (parsed.kind === 'incomplete' && parsed.value == null)) {
    return allowEmpty ? { ok: true, value: '' } : { ok: false };
  }
  if (parsed.kind === 'invalid') return { ok: false };
  const number = clamp ? clampEmptyableNumber(parsed.value, min, max) : parsed.value;
  return { ok: true, value: number };
}

export { sanitizeNumericDraft };
